import React, { Fragment, useMemo, useState, useEffect, useCallback } from "react"
import { Box, FormControl } from '@mui/material';
import { Avatar, Button, Card, FormHelperText, Grid, Icon, Link, Modal, ModalTitle, Radio, RadioGroup, Select, SelectItem, TextField, Typography, designTokens } from "@platform-ui/design-system";
import { SelectModels } from "./SelectModels";
import ConstField from "./ConstField";
import TestConnection from "./TestConnection";
import { Controller, useFormContext } from "react-hook-form";
import BooleanChiclets from "./BooleanChiclets";
import { Destination, ExistingDestination, Form, prepareDestinationWithForm } from "@prequel/react";
import { BillingModelMetaData, VENDOR_TO_CONNECTOR_TITLE_MAPPING, VENDOR_TO_KC_LINK_MAPPINGS, ZDQ_VENDOR_CONNECTOR_TYPES } from "../Constants";
import fetchAuthTokenWithDestination from "../fetchTokens/fetchAuthTokenWithDestination";
import styled from '@emotion/styled';
import { ensureDeltaLakeBucketAuthMethod, findNested } from "../utils";

const StyledConnectorNotConfiguredIcon = styled.div`
         > span {
            font-size : xxx-large
         }; 
        `;

const StyledErrorContainer = styled.div`
    > div > div:last-child {
        padding-bottom: 0;
    }; 
    `;

interface ConnectorAuthenticationProps {
    vendor: ZDQ_VENDOR_CONNECTOR_TYPES;
    models: BillingModelMetaData[];
    destination: Destination;
    destinationForm: Form;
    setDestinationField: (key: keyof Destination, value: string | string[] | boolean | undefined) => void;
    isEditMode?: boolean;
    existingDestinationData?: ExistingDestination;
    onModalClose?: () => void;
}

export const ConnectorAuthentication = ({ vendor, models, destination, destinationForm, setDestinationField, isEditMode = false, existingDestinationData, onModalClose }: ConnectorAuthenticationProps) => {
    const [openEditModal, setOpenEditModal] = useState(isEditMode);
    const [isFormInitialized, setIsFormInitialized] = useState(false);

    const preparedDestination = useMemo(
        () => prepareDestinationWithForm(destination, destinationForm),
        [destination, destinationForm]
    );

    const handleModalClose = () => {
        setOpenEditModal(false);
        onModalClose && onModalClose();
    };

    const {
        control,
        handleSubmit,
        getFieldState,
        register,
        setValue,
        trigger,
        getValues,
        formState: { errors },
    } = useFormContext();

    const getInitialFieldValue = useCallback((fieldName: string) => {
        if (isEditMode && existingDestinationData) {
            const value = findNested(existingDestinationData, fieldName);
            if (value !== undefined && value !== null && typeof value !== 'object') {
                return value;
            }
        }
        return destination[fieldName] || '';
    }, [isEditMode, existingDestinationData, destination]);

    // Get prepared destination with current form values
    const getPreparedDestinationWithFormValues = useCallback(() => {
        const formValues = getValues();
        const updatedDestination = { ...destination, ...formValues };
        const updatedPreparedDestination = prepareDestinationWithForm(updatedDestination, destinationForm);
        const vendorName = updatedPreparedDestination.vendor || preparedDestination.vendor;
        const initialVendorConfig = preparedDestination[vendorName];
        const updatedVendorConfig = updatedPreparedDestination[vendorName];

        if (vendorName) {
            updatedPreparedDestination[vendorName] = {
                ...initialVendorConfig,
                ...updatedVendorConfig,
                auth_method: initialVendorConfig?.auth_method || updatedVendorConfig?.auth_method
            };

        }
        return ensureDeltaLakeBucketAuthMethod(updatedPreparedDestination, updatedDestination);
    }, [getValues, destination, destinationForm, preparedDestination, vendor]);

    // Initialize form in edit mode - populate both react-hook-form state and destination object
    useEffect(() => {
        if (isEditMode && !isFormInitialized && existingDestinationData && openEditModal) {
            destinationForm.forEach((section) => {
                section.fields?.forEach((field) => {
                    if (!field.const) {
                        const fieldValue = getInitialFieldValue(field.name);
                        if (fieldValue) {
                            setValue(field.name, fieldValue, { shouldValidate: false, shouldDirty: false });
                            setDestinationField(field.name, fieldValue);
                        }
                    }
                });
            });
            setIsFormInitialized(true);
            setTimeout(() => trigger(), 100);
        }
    }, [isEditMode, isFormInitialized, existingDestinationData, openEditModal, destinationForm, getInitialFieldValue, setValue, setDestinationField, trigger]);

    return (
        <>
            {!isEditMode && (
                <Card autoDistance
                    id="connector-details"
                    space="large"
                    noPaddingBottom
                    header={
                        <>
                            <Typography variant="h5" body={`${VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor]} Connection`} />
                        </>
                    }
                    titleBar
                    body={
                        <>
                            <Typography color={designTokens.colors.coolGray300} variant='label' body={
                                <>
                                    If you are not sure how to get started or want to read about our recommended configuration, view our documentation on
                                    <Link href={VENDOR_TO_KC_LINK_MAPPINGS[vendor]}
                                        target='_blank' underline='hover'
                                        color="primary"> Zuora Connector for {VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor]}
                                    </Link>
                                </>
                            } />
                            <Grid sx={{ marginTop: '3%', marginBottom: '3%' }} container direction="column" alignItems="center">
                                <Grid item>
                                    <StyledConnectorNotConfiguredIcon>
                                        <Icon body='database_off' variant="outlined" fontSize="large" color="disabled"></Icon>
                                    </StyledConnectorNotConfiguredIcon>
                                </Grid>
                                <Grid item>
                                    <Typography color={designTokens.colors.coolGray400} variant='h6' body="You haven't configured this connector" />
                                </Grid>
                                <Grid item>
                                    <Button dsOnClick={() => { setOpenEditModal((prev) => { return !prev }) }} startIcon='edit' body='Configure' />
                                </Grid>
                            </Grid>
                        </>
                    }>
                </Card >
            )}
            <Modal
                id='connector-authentication-setup-form' open={openEditModal} fullWidth dsOnClose={handleModalClose}
                header={
                    <Box sx={{ '.MuiAvatar-root': { backgroundColor: designTokens.colors.coolGray100 } }}>
                        <ModalTitle icon={<Avatar label=""><Icon body='cable' fontSize="large" /></Avatar>}
                            dsOnClose={handleModalClose} body={`${isEditMode ? 'Edit' : 'Configure'} Your ${VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor]} Connection`} />
                    </Box >
                }
                body={
                    <>
                        {
                            destinationForm?.map((section, sectionIndex) => (
                                <Fragment key={`${section.id}_${sectionIndex}`}>
                                    <Box key={`$Box_{section.id}_${sectionIndex}`} sx={{ '> div': { minWidth: '100%' } }}>
                                        <FormControl>
                                            {section.id > 2 &&
                                                <Box sx={{ borderBottom: '1px solid lightgray', paddingBottom: 3, marginBottom: 3 }}>
                                                    <Box sx={{ marginBottom: '0.5%' }}>
                                                        <Typography variant="title" body={section.title} />
                                                        <FormHelperText>{section.subtitle}</FormHelperText>
                                                    </Box>
                                                    {section?.fields?.map((field, fieldIndex) => {
                                                        if (field.const) {
                                                            return (<Box key={`connector-const-field-${fieldIndex}`} sx={{ margin: '1%', marginLeft: '0' }}><ConstField showDescription={true} field={field} /></Box>);
                                                        } else if (field.form_element === "radio") {
                                                            if (field.type === "boolean") {
                                                                return (
                                                                    <Box key={`connector-boolean-field-${fieldIndex}`} sx={{ marginBottom: 1.5 }}>
                                                                        <Controller
                                                                            name={field.name}
                                                                            control={control}
                                                                            render={({ field: { onChange, value } }) => (
                                                                                <RadioGroup
                                                                                    key={field.name}
                                                                                    required={field.required}
                                                                                    label={field.label}
                                                                                    value={destination[field.name]}
                                                                                    dsOnChange={(e) => {
                                                                                        setDestinationField(field.name, e.target.value === 'false' ? false : true)
                                                                                        onChange(e);
                                                                                    }}
                                                                                    error={!!getFieldState(field.name).error}
                                                                                >
                                                                                    <Grid container direction='row'>
                                                                                        {field?.enum?.map((item) => (
                                                                                            <Grid item>
                                                                                                <Radio
                                                                                                    key={item.key as string}
                                                                                                    checked={!!destination[field.name] === item.key}
                                                                                                    value={item.key}
                                                                                                    label={item.display}
                                                                                                />
                                                                                            </Grid>
                                                                                        ))}
                                                                                    </Grid>
                                                                                    {!!getFieldState(field.name).error &&
                                                                                        <StyledErrorContainer>
                                                                                            <FormHelperText label={errors[field.name]['message'] as string}></FormHelperText>
                                                                                        </StyledErrorContainer>
                                                                                    }
                                                                                    {field.description && <FormHelperText label={field.description}></FormHelperText>}
                                                                                </RadioGroup>
                                                                            )}
                                                                        />
                                                                    </Box>

                                                                );
                                                            }
                                                            return (
                                                                <Box key={`connector-non-boolean-field-${fieldIndex}`} sx={{ marginBottom: 1.5 }}>
                                                                    <BooleanChiclets field={field} control={control} destination={destination} errors={errors} setDestinationField={setDestinationField} register={register} />
                                                                </Box>
                                                            )
                                                        } else if (field.form_element === "select") {
                                                            const items = field.enum?.map(({ key, display, icon_url }) => ({
                                                                key: key.toString(),
                                                                display,
                                                                icon_url
                                                            }));
                                                            const selected = items?.find(
                                                                ({ key }) => key.toString() === destination[field.name]
                                                            );
                                                            return (
                                                                <Box key={`connector-select-field-${fieldIndex}`} sx={{ marginBottom: 1.5 }}>
                                                                    <Controller
                                                                        name={field.name}
                                                                        control={control}
                                                                        rules={{ required: true }}
                                                                        render={({ field: { onChange, value } }) => (
                                                                            <Select
                                                                                key={field.name}
                                                                                {...register(field.name, {
                                                                                    required: { value: field.required, message: `${field.label || 'This'} is a required field.` }
                                                                                })}
                                                                                value={selected?.key || ''}
                                                                                label={field.label}
                                                                                dsOnChange={(event) => {
                                                                                    setDestinationField(field.name, event.target.value as string)
                                                                                    onChange(event)
                                                                                }
                                                                                }
                                                                                required={field.required}
                                                                                helperText={!!getFieldState(field.name).error ? errors[field.name]['message'] as string : ''}
                                                                                error={!!getFieldState(field.name).error}
                                                                            >
                                                                                {items?.map((item, index) => (
                                                                                    <SelectItem key={item.key as string} value={item.key}>
                                                                                        {item.display}
                                                                                    </SelectItem>
                                                                                ))}
                                                                            </Select>
                                                                        )}
                                                                    />
                                                                    {field.description && <FormHelperText label={field.description}></FormHelperText>}
                                                                </Box>
                                                            );
                                                        } else if (field.form_element === "textarea") {
                                                            return (
                                                                <Box key={`connector-textarea-field-${fieldIndex}`} sx={{ marginBottom: 1.5 }}>
                                                                    <Controller
                                                                        name={field.name}
                                                                        control={control}
                                                                        defaultValue={getInitialFieldValue(field.name)}
                                                                        render={({ field: { onChange, value, name, ref } }) => (
                                                                            <>
                                                                                <TextField
                                                                                    key={field.name}
                                                                                    name={name}
                                                                                    maxlength={100000}
                                                                                    {...register(field.name, {
                                                                                        required: { value: field.required, message: `${field.label || 'This'} is a required field.` }
                                                                                    })}
                                                                                    multiline
                                                                                    rows={2}
                                                                                    fullWidth
                                                                                    dsOnChange={(event) => {
                                                                                        setDestinationField(field.name, event.target.value);
                                                                                        onChange(event);
                                                                                    }}
                                                                                    label={field.label}
                                                                                    value={value || ''}
                                                                                    placeholder={field.placeholder || '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'}
                                                                                    type='text'
                                                                                    required={field.required}
                                                                                    error={!!getFieldState(field.name).error}
                                                                                    helperText={!!getFieldState(field.name).error ? errors[field.name]['message'] as string : ''}
                                                                                    inputRef={ref}
                                                                                />
                                                                                {field.description && <FormHelperText label={field.description}></FormHelperText>}
                                                                            </>
                                                                        )}
                                                                    />
                                                                </Box>
                                                            );
                                                        } else if (field.form_element === "input") {
                                                            return (
                                                                <Box key={`connector-input-field-${fieldIndex}`} sx={{ marginBottom: 1.5 }}>
                                                                    <Controller
                                                                        name={field.name}
                                                                        control={control}
                                                                        defaultValue={getInitialFieldValue(field.name)}
                                                                        render={({ field: { onChange, value, name, ref } }) => (
                                                                            <>
                                                                                <TextField
                                                                                    key={field.name}
                                                                                    name={name}
                                                                                    maxlength={10000}
                                                                                    {...register(field.name, {
                                                                                        required: { value: field.required, message: `${field.label || 'This'} is a required field.` }
                                                                                    })}
                                                                                    fullWidth
                                                                                    dsOnChange={(event) => {
                                                                                        setDestinationField(field.name, event.target.value);
                                                                                        onChange(event);
                                                                                    }}
                                                                                    label={field.label}
                                                                                    value={value || ''}
                                                                                    placeholder={field.placeholder}
                                                                                    type={field.input_type || 'text'}
                                                                                    required={field.required}
                                                                                    error={!!getFieldState(field.name).error}
                                                                                    helperText={!!getFieldState(field.name).error ? errors[field.name]['message'] as string : ''}
                                                                                    inputRef={ref}
                                                                                />
                                                                                {field.description && <FormHelperText label={field.description}></FormHelperText>}
                                                                            </>
                                                                        )}
                                                                    />
                                                                </Box>
                                                            );
                                                        }
                                                    })}
                                                </Box>
                                            }
                                        </FormControl>
                                    </Box>
                                </Fragment>
                            ))
                        }
                        {models && models.length > 0 && (
                            <SelectModels
                                setDestinationField={setDestinationField}
                                rows={models}
                                isEditMode={isEditMode}
                                initialProvisionedModelNames={isEditMode ? existingDestinationData?.enabled_models : undefined}
                            />
                        )}
                        <TestConnection setMainModalOpen={setOpenEditModal} fetchTokenWithDestination={fetchAuthTokenWithDestination} vendor={vendor} handleSubmit={handleSubmit} preparedDestination={preparedDestination} getPreparedDestinationWithFormValues={getPreparedDestinationWithFormValues} isEditMode={isEditMode} existingDestinationData={existingDestinationData} />
                    </>
                }
            />
        </>
    )
};